import type { ReactNode } from "react";
import type { AiLayoutAction, AiLayoutPlaceRef } from "@majourneys/shared";
import { activityHrefString } from "../../activities/lib/activity-href";
import { BUTTON_STYLE, SOFT } from "./soft-tokens";

/**
 * Pieces shared by the three answer layouts (ride, place, compare): the two
 * button styles the chat already uses and the one URL builder the site uses.
 */

/** The label key for a place's primary action. All keys exist in 7 locales. */
export function actionLabelKey(action: AiLayoutAction): string {
  switch (action) {
    case "book_table":
      return "ai_concierge.action_book_table";
    case "book_ride":
      return "ai_concierge.action_book_ride";
    case "view_place":
      return "ai_concierge.action_view_place";
    default:
      return "ai_concierge.action_book";
  }
}

/**
 * A table is free: a restaurant never shows a price on any chat surface,
 * whatever the catalogue row says (owner rule; restaurant prices in the
 * catalogue are stale). Booking a table is the giveaway, the segment and the
 * category are the same test the rest of the site uses.
 */
export function isRestaurantRef(
  ref:
    | (Partial<Pick<AiLayoutPlaceRef, "action">> &
        Partial<Record<"segment" | "categorySlug", string | null>>)
    | null
    | undefined,
): boolean {
  if (!ref) return false;
  return ref.action === "book_table" || ref.segment === "restaurants" || ref.categorySlug === "restaurants";
}

/** The canonical 4-segment page, built with the site's own helper. */
export function placeHref(
  ref: Pick<AiLayoutPlaceRef, "slug" | "citySlug" | "categorySlug" | "segment">,
  lang: string,
): string {
  return activityHrefString({
    lang,
    citySlug: ref.citySlug,
    categorySlug: ref.categorySlug,
    segment: ref.segment,
    slug: ref.slug,
  });
}

/**
 * A link that looks like the chat's buttons: the navy gradient pill the match
 * preview uses for its primary action, or the lit control with one navy
 * hairline for a secondary one. 44px tall, well over the 24px floor.
 */
export function LayoutButton({
  href,
  primary,
  children,
  ariaLabel,
}: {
  href: string;
  primary?: boolean;
  children: ReactNode;
  ariaLabel?: string;
}) {
  return (
    <a
      href={href}
      aria-label={ariaLabel}
      data-variant={primary ? "primary" : "secondary"}
      className="mj-press inline-flex min-h-[44px] items-center justify-center rounded-full px-5 text-center text-[13.5px] font-semibold"
      style={
        primary
          ? {
              background: SOFT.buttonFace,
              color: "#fff",
              boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${SOFT.buttonInset}`,
            }
          : {
              // A control: the soft-bevel white face with one navy hairline, never the band.
              ...BUTTON_STYLE,
              color: SOFT.ink,
            }
      }
    >
      {children}
    </a>
  );
}

/**
 * The money line every layout shares when a place is a restaurant: green,
 * never a figure.
 */
export function FreeToReserve({ label }: { label: string }) {
  return (
    <span className="text-[12.5px] font-semibold" style={{ color: SOFT.ok }}>
      {label}
    </span>
  );
}

/**
 * The small gold caps label inside the cards ("Our pick", "Worth knowing"),
 * the same one the editorial answer's advice slots wear.
 */
export function LayoutLabel({ children }: { children: ReactNode }) {
  return (
    <div
      className="text-[11px] font-extrabold uppercase tracking-[1.2px]"
      style={{ color: SOFT.goldInk }}
    >
      {children}
    </div>
  );
}
